import Typography from "@mui/material/Typography";
import { Theme, alpha, styled } from "@mui/material/styles";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";

import { accentColor, borderColor, isDarkTheme } from "../../utils/matchOdds";
import { SurfaceCard } from "./shared";

/* The promotions page: one colour per kind of offer, carried by its card, icon and price. */

export type PromoTone = "boost" | "free_bet" | "profit_boost" | "insurance";

export const promoToneColor = (theme: Theme, tone: PromoTone): string => {
  switch (tone) {
    case "boost":
      return theme.palette.warning.main;
    case "free_bet":
      return theme.palette.success.main;
    case "profit_boost":
      return accentColor(theme);
    default:
      return isDarkTheme(theme) ? MASCOT_PALETTE.sky : MASCOT_PALETTE.deepInk;
  }
};

/** An offer. `spent`: a boost already used, kept on the page but stepped back. */
export const PromoCard = styled(SurfaceCard, {
  shouldForwardProp: (prop) => prop !== "tone" && prop !== "spent",
})<{ tone: PromoTone; spent?: boolean }>(({ theme, tone, spent }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  height: "100%",
  padding: 14,
  // A band in the offer's colour along the top, so the kind reads before the words do.
  borderTop: `3px solid ${spent ? borderColor(theme) : promoToneColor(theme, tone)}`,
  opacity: spent ? 0.65 : 1,
}));

export const PromoIcon = styled("span", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: PromoTone }>(({ theme, tone }) => {
  const ink = promoToneColor(theme, tone);
  return {
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: "10px",
    color: ink,
    background: alpha(ink, isDarkTheme(theme) ? 0.16 : 0.1),
  };
});

/** The headline figure: the boosted price, the free bet's amount, the boost's percent. */
export const PromoFigure = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: PromoTone }>(({ theme, tone }) => ({
  flexShrink: 0,
  fontSize: 22,
  lineHeight: 1.1,
  fontWeight: 800,
  fontVariantNumeric: "tabular-nums",
  letterSpacing: -0.3,
  color: promoToneColor(theme, tone),
}));

/** The offer's terms as label / value pairs, values lined up on the right. */
export const TermList = styled("dl")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  columnGap: 12,
  rowGap: 4,
  margin: 0,
  padding: "10px 0 0",
  borderTop: `1px solid ${borderColor(theme)}`,
  fontSize: 12,
  lineHeight: "18px",
  "& dt": { color: theme.palette.text.secondary, whiteSpace: "nowrap" },
  "& dd": {
    margin: 0,
    minWidth: 0,
    textAlign: "right",
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

/** Offer grids are lists, so a reader hears "3 items" before the first card. */
export const offerListSx = { listStyle: "none", m: 0, p: 0 };
